import * as Sentry from "@sentry/react"
import { getValidTokenForRequest } from "@/lib/token-validation"
import { useAppStore } from "./app-store"
import { ApiRequestError, ExpiredTokenError } from "./errors"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

export const VerificationStatusMap = {
  UNVERIFIED: "unverified",
  EMAIL_CODE_SENT: "email_code_sent",
  EMAIL_VERIFIED: "email_verified",
  CARD_ID_UPLOAD: "card_id_upload",
  CARD_ID_VERIFIED: "card_id_verified",
  VIDEO_UPLOAD: "video_upload",
  VIDEO_VERIFIED: "video_verified",
  ATTACHMENTS: "attachments",
  ATTACHMENTS_VERIFIED: "attachments_verified",
  RESERVATION_OF_RIGHTS: "reservation_of_rights",
  RESERVATION_OF_RIGHTS_VERIFIED: "reservation_of_rights_verified",
  BANK_INFO: "bank_info",
  BANK_INFO_VERIFIED: "bank_info_verified",
  VERIFIED: "verified",
} as const

export const SignatureServiceMap = {
  ECERT: "ecert",
  FIRME_SIMPLE: "firme_simple",
} as const

export const DocumentStatusMap = {
  IN_PROCESS: "in_process",
  SIGNED: "signed",
  REJECTED: "rejected",
} as const

export const SigneeStatusMap = {
  IN_PROCESS: "in_process",
  SIGNING: "signing",
  FAILED: "failed",
  SIGNED: "signed",
  REJECTED: "rejected",
} as const

type VerificationStatus =
  (typeof VerificationStatusMap)[keyof typeof VerificationStatusMap]
type SignatureService =
  (typeof SignatureServiceMap)[keyof typeof SignatureServiceMap]
type DocumentStatus = (typeof DocumentStatusMap)[keyof typeof DocumentStatusMap]
type SigneeStatus = (typeof SigneeStatusMap)[keyof typeof SigneeStatusMap]

export type DocumentSignee = {
  id: number
  email: string
  status: SigneeStatus
  verification: string[]
  verification_status: VerificationStatus
  created_at: string // TODO: parse as a date if needed
  updated_at: string // TODO: parse as a date if needed
}

export type SigningDocument = {
  id: number
  name: string
  service: SignatureService
  status: DocumentStatus
  created_at: string // TODO: parse as a date if needed
  updated_at: string // TODO: parse as a date if needed
}

export type EmailVerificationStatus =
  | {
      status: Extract<VerificationStatus, "unverified" | "email_verified">
      sent_at: null
      expires_at: null
    }
  | {
      status: Extract<VerificationStatus, "email_code_sent">
      sent_at: string // TODO: parse as date
      expires_at: string // TODO: parse as date
    }

export type CardIdVerificationStatus = {
  status: VerificationStatus
  front_side_url: string | null
  rear_side_url: string | null
}

export type VideoVerificationStatus = {
  status: VerificationStatus
  legend: string | null
  video_url: string | null
}

export type ReservationOfRightsStatus = {
  status: VerificationStatus
  reservation_of_rights: boolean | null
  reservation_of_rights_content: string | null
}

export type BankAccountType = "sight" | "checking" | "savings"

export type BankResponse = {
  id: number
  name: string
  code: string
  disabled: boolean
}

export type BankAccountResponse = {
  id: number
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
  bank: BankResponse
  confirmed_at: string | null
  holder_prefilled: boolean
}

export type BankAccountStatus = {
  status: VerificationStatus
  bank_account: BankAccountResponse | null
}

export type BankAccountCreate = {
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
}

export type GetDocumentFileResponse = {
  download_url: string
}

export type NextStepResponse = {
  next_step: string | null
  redirect_url: string | null
}

export type AttachmentItem = {
  id: number
  name: string
  mime_type: string
  download_url: string
  description: string | null
}

export type GetNextVerificationStepInput = {
  token_id: string
}

// Payment types
export type PaymentStatus = "pending" | "approved" | "rejected" | "cancelled"

export interface PaymentStatusResponse {
  required: boolean
  amount?: number
  status?: PaymentStatus
}

export interface ProcessPaymentRequest {
  token: string
  payment_method_id: string
  installments: number
  payer: {
    email: string
    identification: {
      type: string
      number: string
    }
  }
}

export interface ProcessPaymentResponse {
  status: PaymentStatus
  status_detail: string
  payment_id: string
}

export interface CreatePaymentPreferenceResponse {
  preference_id: string
}

export type ValidationSignee = {
  id: number
  full_name: string
  rut_masked: string
  status: SigneeStatus
  signed_at: string | null
}

export type ValidationResponse = {
  document: {
    name: string
    status: DocumentStatus
    signed_at: string | null
  }
  scanned_signee_id: number
  signees: ValidationSignee[]
  has_download: boolean
}

class ApiClient {
  async get<TData>(path: string, token?: string): Promise<TData> {
    const headers = new Headers({
      "Content-Type": "application/json",
    })

    if (token) headers.set("Authorization", `Bearer ${token}`)

    return fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers,
    }).then(async (response) => {
      if (response.ok) {
        return response.json() as Promise<TData>
      }

      let body
      try {
        body = await response.json()
      } catch (jsonError) {
        // Log to Sentry when we encounter non-JSON error responses
        Sentry.captureException(jsonError, {
          tags: {
            errorType: "non_json_error_response",
            httpMethod: "GET",
          },
          extra: {
            url: `${API_BASE_URL}${path}`,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            responseType: response.type,
            originalError: jsonError,
          },
        })

        body = {
          type: "NetworkError",
          message:
            "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
        }
      }
      throw new ApiRequestError(body, response)
    })
  }

  async post<TData>(
    path: string,
    json?: Record<string, any> | null, // eslint-disable-line @typescript-eslint/no-explicit-any
    token?: string,
  ): Promise<TData> {
    const headers = new Headers({
      "Content-Type": "application/json",
    })

    if (token) headers.set("Authorization", `Bearer ${token}`)

    return fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      body: json ? JSON.stringify(json) : undefined,
      headers,
    }).then(async (response) => {
      if (response.ok) {
        if (response.status == 204) {
          return Promise.resolve(null) as Promise<TData>
        }
        return response.json() as Promise<TData>
      }

      let body
      try {
        body = await response.json()
      } catch (jsonError) {
        // Log to Sentry when we encounter non-JSON error responses
        Sentry.captureException(jsonError, {
          tags: {
            errorType: "non_json_error_response",
            httpMethod: "POST",
          },
          extra: {
            url: `${API_BASE_URL}${path}`,
            requestBody: json,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            responseType: response.type,
            originalError: jsonError,
          },
        })

        body = {
          type: "NetworkError",
          message:
            "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
        }
      }
      throw new ApiRequestError(body, response)
    })
  }

  async put<TData>(
    path: string,
    json?: Record<string, any> | null, // eslint-disable-line @typescript-eslint/no-explicit-any
    token?: string,
  ): Promise<TData> {
    const headers = new Headers({
      "Content-Type": "application/json",
    })

    if (token) headers.set("Authorization", `Bearer ${token}`)

    return fetch(`${API_BASE_URL}${path}`, {
      method: "PUT",
      body: json ? JSON.stringify(json) : undefined,
      headers,
    }).then(async (response) => {
      if (response.ok) {
        if (response.status == 204) {
          return Promise.resolve(null) as Promise<TData>
        }
        return response.json() as Promise<TData>
      }

      let body
      try {
        body = await response.json()
      } catch (jsonError) {
        // Log to Sentry when we encounter non-JSON error responses
        Sentry.captureException(jsonError, {
          tags: {
            errorType: "non_json_error_response",
            httpMethod: "PUT",
          },
          extra: {
            url: `${API_BASE_URL}${path}`,
            requestBody: json,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            responseType: response.type,
            originalError: jsonError,
          },
        })

        body = {
          type: "NetworkError",
          message:
            "Ha ocurrido un error de conexión. Por favor, inténtalo de nuevo.",
        }
      }
      throw new ApiRequestError(body, response)
    })
  }
}

const api = new ApiClient()

/**
 * Helper function to get a valid token for API requests
 * This prevents race conditions by getting the most up-to-date token from the store
 */
const getTokenForRequest = (token_id: string): string | undefined => {
  return getValidTokenForRequest(() => useAppStore.getState().tokens[token_id])
}

export const getToken = async ({
  token_id,
  code,
}: {
  token_id: string
  code: string
}) =>
  api.post<{ token: string }>("/token", { token_id, code }).catch((e) => {
    if (e.status === 401) throw new ExpiredTokenError()
    throw e
  })

export const getDocument = ({
  document_id,
  token_id,
}: {
  document_id: number
  token_id: string
}) =>
  api
    .get<SigningDocument>(
      `/sign/documents/${document_id}`,
      getTokenForRequest(token_id),
    )
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getSignee = ({
  document_id,
  signee_id,
  token_id,
}: {
  document_id: number
  signee_id: number
  token_id: string
}) =>
  api
    .get<DocumentSignee>(
      `/sign/documents/${document_id}/signees/${signee_id}`,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getEmailVerificationStatus = ({
  token_id,
}: {
  token_id: string
}) =>
  api
    .get<EmailVerificationStatus>(
      "/sign/verify/email/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const sendVerificationCode = ({ token_id }: { token_id: string }) =>
  api
    .post<EmailVerificationStatus>(
      "/sign/verify/email",
      null,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateEmailCode = ({
  code,
  token_id,
}: {
  code: number
  token_id: string
}) =>
  api
    .post<EmailVerificationStatus>(
      "/sign/verify/email/validate",
      { code },
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateCardUpload = ({
  file_id,
  token_id,
}: {
  file_id: number
  token_id: string
}) =>
  api
    .post<CardIdVerificationStatus>(
      "/sign/verify/card-id/validate",
      { file_id },
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const uploadCardFile = ({
  kind,
  file,
  token_id,
}: {
  kind: "card_id_front" | "card_id_rear" | "video_confirmation"
  file: File
  token_id: string
}) =>
  api
    .post<{
      file_id: number
      upload_url: string
    }>(
      "/sign/verify/card-id",
      { kind, mime_type: file.type },
      getTokenForRequest(token_id),
    )
    .then(async ({ file_id, upload_url }) => {
      return fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      }).then(async (response) => {
        if (response.ok) {
          return validateCardUpload({ file_id, token_id })
        }

        let body
        try {
          body = await response.json()
        } catch (jsonError) {
          // Log to Sentry when we encounter non-JSON error responses in file upload
          Sentry.captureException(jsonError, {
            tags: {
              errorType: "non_json_error_response",
              httpMethod: "PUT",
              operation: "file_upload",
            },
            extra: {
              url: upload_url,
              fileType: file.type,
              fileName: file.name,
              fileSize: file.size,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries(response.headers.entries()),
              responseType: response.type,
              originalError: jsonError,
            },
          })

          body = {
            type: "NetworkError",
            message:
              "Ha ocurrido un error subiendo el archivo. Por favor, inténtalo de nuevo.",
          }
        }
        throw new ApiRequestError(body, response)
      })
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getCardIdVerificationStatus = ({
  token_id,
}: {
  token_id: string
}) =>
  api
    .get<CardIdVerificationStatus>(
      "/sign/verify/card-id/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getVideoVerificationStatus = ({
  token_id,
}: {
  token_id: string
}) =>
  api
    .get<VideoVerificationStatus>(
      "/sign/verify/video/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateVideoUpload = ({
  file_id,
  token_id,
}: {
  file_id: number
  token_id: string
}) =>
  api
    .post<CardIdVerificationStatus>(
      "/sign/verify/video/validate",
      { file_id },
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const uploadVideoFile = ({
  file,
  token_id,
  onUploadProgress,
}: {
  file: File
  token_id: string
  onUploadProgress: (progress: number) => void
}) =>
  api
    .post<{
      file_id: number
      upload_url: string
    }>(
      "/sign/verify/video",
      { mime_type: file.type, kind: "video_confirmation" },
      getTokenForRequest(token_id),
    )
    .then(async ({ file_id, upload_url }) => {
      const xhr = new XMLHttpRequest()
      return new Promise<{ file_id: number; uploaded: boolean }>((resolve) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = event.loaded / event.total
            onUploadProgress(progress * 100)
          }
        })
        xhr.addEventListener("loadend", () => {
          resolve({
            file_id,
            uploaded: xhr.readyState === 4 && xhr.status === 200,
          })
        })
        xhr.open("PUT", upload_url, true)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      }).then((res) => {
        if (res.uploaded) {
          return validateVideoUpload({ file_id, token_id })
        }
      })
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getNextVerificationStep = ({
  token_id,
}: GetNextVerificationStepInput) =>
  api
    .post<NextStepResponse>(
      "/sign/verify/continue",
      null,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getDocumentFile = ({
  document_id,
  token_id,
}: {
  document_id: number
  token_id: string
}) =>
  api
    .get<GetDocumentFileResponse>(
      `/sign/documents/${document_id}/file`,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const downloadDocumentFile = async ({
  document_id,
  filename,
  token_id,
}: {
  document_id: number
  filename: string
  token_id: string
}) => {
  return getDocumentFile({ document_id, token_id }).then(async (data) => {
    return fetch(data.download_url, {
      method: "GET",
    })
      .then(async (response) => {
        if (response.ok) {
          return response.blob()
        }

        let body
        try {
          body = await response.json()
        } catch (jsonError) {
          // Log to Sentry when we encounter non-JSON error responses in document download
          Sentry.captureException(jsonError, {
            tags: {
              errorType: "non_json_error_response",
              httpMethod: "GET",
              operation: "document_download",
            },
            extra: {
              url: data.download_url,
              documentId: document_id,
              filename: filename,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries(response.headers.entries()),
              responseType: response.type,
              originalError: jsonError,
            },
          })

          body = {
            type: "NetworkError",
            message:
              "Ha ocurrido un error descargando el documento. Por favor, inténtalo de nuevo.",
          }
        }
        throw new ApiRequestError(body, response)
      })
      .then((blob) => {
        return new File([blob], filename, {
          type: "application/pdf",
        })
      })
  })
}

export const uploadSignatureFile = async ({
  document_id,
  file,
  token_id,
}: {
  document_id: number
  file: File
  token_id: string
}) =>
  api
    .post<{ upload_url: string }>(
      `/sign/documents/${document_id}/signature`,
      { mime_type: file.type },
      getTokenForRequest(token_id),
    )
    .then(async ({ upload_url }) =>
      fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      }),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const signDocument = async ({
  document_id,
  signature,
  token_id,
}: {
  document_id: number
  signature: File
  token_id: string
}) =>
  uploadSignatureFile({ file: signature, document_id, token_id })
    .then(() =>
      api
        .post(
          `/sign/documents/${document_id}/sign`,
          null,
          getTokenForRequest(token_id),
        )
        .catch((e: ApiRequestError) => {
          if (e.status === 401) throw new ExpiredTokenError()
          throw e
        }),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getReservationOfRightsStatus = ({
  token_id,
}: {
  token_id: string
}) =>
  api
    .get<ReservationOfRightsStatus>(
      "/sign/verify/rights/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateReservationOfRights = (data: {
  reservation_of_rights: boolean
  reservation_of_rights_content: string | null
  token_id: string
}) =>
  api
    .post<ReservationOfRightsStatus>(
      "/sign/verify/rights/validate",
      {
        reservation_of_rights: data.reservation_of_rights,
        reservation_of_rights_content: data.reservation_of_rights_content,
      },
      useAppStore.getState().tokens[data.token_id],
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateAttachments = (data: { token_id: string }) =>
  api
    .post<null>(
      "/sign/verify/attachments/validate",
      null,
      useAppStore.getState().tokens[data.token_id],
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const listSigneeAttachments = ({
  document_id,
  signee_id,
  token_id,
}: {
  document_id: number
  signee_id: number
  token_id: string
}) =>
  api
    .get<AttachmentItem[]>(
      `/sign/documents/${document_id}/signees/${signee_id}/attachments`,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getBankAccountsStatus = ({ token_id }: { token_id: string }) =>
  api
    .get<BankAccountStatus>(
      "/sign/verify/bank-account/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const createBankAccount = ({
  holder_id,
  holder_name,
  number,
  type,
  bank_id,
  token_id,
}: BankAccountCreate & {
  token_id: string
}) =>
  api
    .put<BankAccountStatus>(
      "/sign/verify/bank-account",
      { holder_id, holder_name, number, type, bank_id },
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateBankAccount = ({ token_id }: { token_id: string }) =>
  api
    .post<BankAccountStatus>(
      "/sign/verify/bank-account/validate",
      null,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getBanks = () =>
  api.get<BankResponse[]>("/banks").catch((e: ApiRequestError) => {
    throw e
  })

// Payment API functions
export const getPaymentStatus = ({ token_id }: { token_id: string }) =>
  api
    .get<PaymentStatusResponse>(
      "/sign/payment/status",
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const createPaymentPreference = ({ token_id }: { token_id: string }) =>
  api
    .post<CreatePaymentPreferenceResponse>(
      "/sign/payment/preference",
      null,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getValidation = (token: string) =>
  api.get<ValidationResponse>(`/validations/${encodeURIComponent(token)}`)

export const getValidationDownloadUrl = (token: string) =>
  `${API_BASE_URL}/validations/${encodeURIComponent(token)}/download`

export const processPayment = ({
  token_id,
  ...body
}: { token_id: string } & ProcessPaymentRequest) =>
  api
    .post<ProcessPaymentResponse>(
      "/sign/payment/process",
      body,
      getTokenForRequest(token_id),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })
